'use client';
import { useContext, useEffect, useRef, useState } from 'react';
import { useWatchlistContext } from '@benzinga/user-context';
import { SessionContext } from '@benzinga/session-context';
import { Tooltip } from '@benzinga/core-ui';
import { FaBell, FaPlus } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import { WatchlistSelector } from './WatchlistSelector';
import { getWatchlistDailyChangePercent } from '../data';
import { WATCHLIST_PANEL_TOOLBAR_CLASS } from './FinancialTable/watchlistQuotesTableChrome';
import {
  widgetGhostButtonClassName,
  widgetPrimaryButtonClassName,
} from '../../../edge/dashboard/_components/widgetChrome';

const formatSignedPercent = (value: number) => `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(2)}%`;

export const WatchlistDailyChangeBadge = ({ percent }: { percent: number | null }) => {
  const isUp = percent !== null && percent >= 0;

  return (
    <span
      aria-hidden={percent === null}
      className={`inline-flex h-5 min-h-5 min-w-[5.75rem] shrink-0 items-center rounded-full px-2.5 text-xs font-semibold tabular-nums leading-none ${
        percent === null
          ? 'invisible'
          : isUp
            ? 'bg-bzgreen-500/10 text-bzgreen-500'
            : 'bg-bzred-500/10 text-bzred-500'
      }`}
    >
      {percent !== null ? (
        <>
          {formatSignedPercent(percent)}
          <span className="ml-1 font-medium opacity-70">today</span>
        </>
      ) : (
        '0.00% today'
      )}
    </span>
  );
};

export const WatchlistTopBar = () => {
  const { selectedWatchlist, setAddTickerModal, setEditModal, setShowNotificationsModal } = useWatchlistContext();
  const session = useContext(SessionContext);
  const [dailyChangePercent, setDailyChangePercent] = useState<number | null>(null);
  const isOverview = selectedWatchlist?.watchlistId === 'overview';
  const symbolCount = selectedWatchlist?.symbols.length ?? 0;

  const selectedWatchlistId = selectedWatchlist?.watchlistId;
  const selectedSymbolsKey = selectedWatchlist?.symbols.map(symbol => symbol.symbol).join(',') ?? '';
  const selectedWatchlistRef = useRef(selectedWatchlist);
  selectedWatchlistRef.current = selectedWatchlist;

  useEffect(() => {
    const abortController = new AbortController();
    const watchlist = selectedWatchlistRef.current;

    const loadDailyChange = async () => {
      if (!watchlist || watchlist.symbols.length === 0) {
        setDailyChangePercent(null);
        return;
      }

      const dailyChange = await getWatchlistDailyChangePercent(watchlist, session.getSession());
      if (abortController.signal.aborted) return;
      setDailyChangePercent(dailyChange?.percent ?? null);
    };

    loadDailyChange();

    return () => {
      abortController.abort();
    };
  }, [selectedWatchlistId, selectedSymbolsKey, session]);

  const handleNotificationsClick = () => {
    setShowNotificationsModal({ show: true, watchlist: isOverview ? null : selectedWatchlist });
  };

  const handleEditClick = () => {
    if (!selectedWatchlist || isOverview) return;
    setEditModal({ show: true, watchlist: selectedWatchlist });
  };

  const editButton = (
    <button
      type="button"
      disabled={isOverview}
      className={`${widgetGhostButtonClassName} w-full justify-center sm:w-auto`}
      onClick={handleEditClick}
    >
      <FiEdit2 size={14} />
      Edit
    </button>
  );

  return (
    <div className={WATCHLIST_PANEL_TOOLBAR_CLASS}>
      <div className="min-w-0">
        <div className="flex min-h-7 min-w-0 flex-wrap items-center gap-2.5">
          <WatchlistSelector />
          <WatchlistDailyChangeBadge percent={dailyChangePercent} />
        </div>
        <p className="mt-1 text-xs leading-none text-slate-500">
          {symbolCount} {symbolCount === 1 ? 'symbol' : 'symbols'}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
        <button
          type="button"
          className={`${widgetGhostButtonClassName} justify-center`}
          onClick={handleNotificationsClick}
        >
          <FaBell size={12} />
          Alerts
        </button>
        {isOverview ? (
          <Tooltip content="Select a watchlist to edit" size="sm">
            <span className="block w-full sm:w-auto">{editButton}</span>
          </Tooltip>
        ) : (
          editButton
        )}
        <button
          type="button"
          className={`col-span-2 ${widgetPrimaryButtonClassName} justify-center sm:col-span-1`}
          onClick={() => setAddTickerModal({ show: true, ticker: null, isLoading: false })}
        >
          <FaPlus size={12} />
          Add tickers
        </button>
      </div>
    </div>
  );
};
